<template>
  <!-- Loading -->
  <LoadingDiv v-if="isInitialLoading" />

  <!-- Claim authorization history -->
  <template v-else>
    <div class="card z-depth-0" v-for="claim in claims" :key="claim.id">
      <div class="card-panel z-depth-0">
        <div class="row">
          <div class="col s3 hide-on-small-only">
            <h6>
              {{ claim.encounter.name }}
              <small><span class="blue-text">{{
                showAgeSex(claim.encounter.dob, claim.encounter.sex)
              }}</span></small>
              <br />
              <small>{{ claim.encounter.enrollee_number }}</small>
              <br />
              <small>({{ claim.encounter.client_name }})</small>
            </h6>
          </div>
          <div class="col s5 m2">
            <p>{{ claim.encounter.provider.name }}</p>
          </div>
          <div class="col s7 m2">
            <p>
              <b>{{ claim.encounter.diagnosis }}</b>
            </p>
          </div>
          <div class="col s12 m2">
            <p>
              {{ claim.item }}
              <span class="black-text">(x<b>{{ claim.sent_qty }}</b>)</span>
              <br />
              <span v-if="claim.sent_price" :class="{
                'amber-text text-darken-4': claim.authorization === 'z' && claim.status === 'a',
                'black-text': !(claim.authorization === 'z' && claim.status === 'a'),
              }">&#8358;{{
                Number(claim.sent_price)
                  .toFixed(2)
                  .toString()
                  .replace(/\B(?=(\d{3})+(?!\d))/g, ",")
              }}
              </span>
            </p>
          </div>
          <div class="col s7 m2">
            <p>
              <span v-if="claim.authorization === 'z'">
                <span class="chip" v-if="claim.status === 'a'">Pending</span>
                <span class="chip red lighten-2" v-else>Declined</span>
              </span>

              <span v-else>{{ claim.authorization_code }}<br />
                <span class="chip green lighten-2" v-if="claim.approved_price"><b>&#8358;{{
                  Number(claim.approved_price)
                    .toFixed(2)
                    .toString()
                    .replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                }}</b>
                </span>
                <span v-else>&#8358;0.00</span>
              </span>
            </p>
          </div>
          <div class="col s5 m1">
            <p class="right" :class="{
              'black-text': claim.authorization === 'z',
              'green-text': claim.authorization !== 'z',
            }">
              {{
                new Date(claim.created_at).toLocaleString(undefined, {
                  year: "numeric",
                  month: "short",
                  day: "numeric",
                })
              }}
            </p>
          </div>
        </div>
      </div>
    </div>

    <div class="section center" v-if="nextLoad">
      <button
        class="btn-flat waves-effect history-load-more"
        :class="{ disabled: isLoading }"
        @click.prevent="handlePage(nextLoad)"
      >
        <span v-if="!isLoading">Load more history</span>
        <span v-else>Loading history...</span>
      </button>
      <div class="preloader-wrapper small active history-inline-loader" v-if="isLoading">
        <div class="spinner-layer spinner-blue-only">
          <div class="circle-clipper left">
            <div class="circle"></div>
          </div>
          <div class="gap-patch">
            <div class="circle"></div>
          </div>
          <div class="circle-clipper right">
            <div class="circle"></div>
          </div>
        </div>
      </div>
    </div>
  </template>

  <!-- No History Found -->
  <template v-if="!hideUnFound">
    <div class="claim-history-empty-note" v-if="!claims.length && !isLoading && hasLoaded">
      <i class="material-icons">info_outline</i>
      <span>Authorization history is not available for this selected claim.</span>
    </div>
  </template>
</template>

<script>
import { ref, computed, onMounted } from "vue";
import { ageSex } from "@/composables/fxLibrary/coreFx";
import getItems from "@/composables/oneProvider/getItems";
import LoadingDiv from "@/components/LoadingDiv.vue";

export default {
  name: "HistoryClaimComponent",
  components: { LoadingDiv },
  props: ["enrolleeNumber", "principalId", "hideUnFound"],
  setup(props) {
    const response = ref(null);
    const error = ref(false);
    const isLoading = ref(false);
    const hasLoaded = ref(false);
    const nextLoad = ref(null);

    // get function
    const getItemsFromApi = (table) => {
      (async () => {
        const { res, err } = await getItems(table);

        if (res.value) {
          if (response.value) {
            response.value = [...response.value, ...res.value.data.data];
          } else {
            response.value = res.value.data.data;
          }

          if (res.value.data.last_page > res.value.data.current_page) {
            nextLoad.value = res.value.data.current_page + 1;
          } else {
            nextLoad.value = null;
          }
        } else {
          error.value = err.value;
        }

        // loading is complete
        isLoading.value = false;
        hasLoaded.value = true;
      })();
    };

    // start
    onMounted(() => {
      handlePage(1);
    });

    // ageSex
    const showAgeSex = (dob, sex) => {
      const { fx } = ageSex(dob, sex);
      return fx.value;
    };

    // switch page
    const handlePage = (page) => {
      if (isLoading.value) {
        return;
      }

      // start loading
      isLoading.value = true;

      const principalId = Number(props.principalId);
      const principalQuery = principalId > 0 ? `&principal_id=${principalId}` : "";

      // get new page items from function
      getItemsFromApi(
        `claim/auth/enrollee?e=${props.enrolleeNumber}${principalQuery}&page=${page}`
      );
    };

    const isInitialLoading = computed(() => {
      return isLoading.value && !hasLoaded.value;
    });

    // output results
    const claims = computed(() => {
      if (!response.value) {
        return [];
      } else {
        return response.value;
      }
    });

    return {
      showAgeSex,
      error,
      isLoading,
      isInitialLoading,
      hasLoaded,
      nextLoad,
      handlePage,
      claims,
    };
  },
};
</script>

<style scoped>
.history-load-more {
  text-transform: none;
}

.history-inline-loader {
  display: block;
  margin: 10px auto 0;
}

.claim-history-empty-note {
  align-items: center;
  background: #f8fafb;
  border: 1px solid #e1e8eb;
  border-radius: 6px;
  color: #607d8b;
  display: flex;
  gap: 8px;
  justify-content: center;
  min-height: 90px;
  padding: 18px;
}

.claim-history-empty-note i {
  color: #00897b;
  font-size: 20px;
}
</style>
